/**
 * GroupInfoModal Component
 *
 * Modal for viewing and managing group conversation details.
 * Features group name/avatar editing, member list, and leave group option.
 */

import React, { useState, useCallback } from 'react';
import {
  Users,
  Pencil,
  Check,
  X,
  UserPlus,
  LogOut,
  Loader2,
  Trash2,
  Crown,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useMessagingActions } from '@/stores/messaging';
import { useAuthStore } from '@/stores/auth/auth-store';
import type { Conversation, ConversationParticipant } from '@/types/messaging';
import { AddMembersModal } from './AddMembersModal';

interface GroupInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversation: Conversation;
}

/**
 * Get initials from participant
 */
const getInitials = (participant: ConversationParticipant): string => {
  if (participant.firstName && participant.lastName) {
    return `${participant.firstName.charAt(0)}${participant.lastName.charAt(0)}`.toUpperCase();
  }
  if (participant.firstName) {
    return participant.firstName.charAt(0).toUpperCase();
  }
  return participant.username.charAt(0).toUpperCase();
};

/**
 * Get display name from participant
 */
const getDisplayName = (participant: ConversationParticipant): string => {
  if (participant.firstName && participant.lastName) {
    return `${participant.firstName} ${participant.lastName}`;
  }
  if (participant.firstName) {
    return participant.firstName;
  }
  return participant.username;
};

/**
 * MemberListItem - Individual member row
 */
interface MemberListItemProps {
  participant: ConversationParticipant;
  isCreator: boolean;
  isCurrentUser: boolean;
  canRemove: boolean;
  onRemove: () => void;
  isRemoving: boolean;
}

const MemberListItem: React.FC<MemberListItemProps> = ({
  participant,
  isCreator,
  isCurrentUser,
  canRemove,
  onRemove,
  isRemoving,
}) => {
  const displayName = getDisplayName(participant);
  const initials = getInitials(participant);

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="relative">
        <Avatar className="h-10 w-10">
          <AvatarImage src={participant.avatar} alt={displayName} />
          <AvatarFallback
            style={{
              backgroundColor: 'hsl(var(--primary) / 0.1)',
              color: 'hsl(var(--primary))',
            }}
            className="text-sm font-medium"
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        {participant.isOnline && (
          <span
            className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-card"
            style={{ backgroundColor: 'hsl(var(--primary))' }}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">
            {displayName}
            {isCurrentUser && <span className="text-gray-500"> (You)</span>}
          </p>
          {isCreator && (
            <Crown className="h-3.5 w-3.5 text-amber-500" aria-label="Group creator" />
          )}
        </div>
        <p className="text-xs text-gray-500">@{participant.username}</p>
      </div>

      {canRemove && !isCurrentUser && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={isRemoving}
          className="text-gray-400 hover:text-destructive hover:bg-destructive/10"
        >
          {isRemoving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  );
};

export const GroupInfoModal: React.FC<GroupInfoModalProps> = ({
  open,
  onOpenChange,
  conversation,
}) => {
  const currentUser = useAuthStore((state) => state.user);
  const { updateGroup, removeMemberFromGroup, leaveGroup } = useMessagingActions();

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(conversation.groupName || '');
  const [isSaving, setIsSaving] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);

  const isCreator = currentUser?.id === conversation.createdBy;
  const members = conversation.participants || [];
  const memberCount = members.length;

  /**
   * Handle saving group name
   */
  const handleSaveName = useCallback(async () => {
    if (!editedName.trim() || editedName.trim() === conversation.groupName) {
      setIsEditing(false);
      setEditedName(conversation.groupName || '');
      return;
    }

    setIsSaving(true);
    try {
      await updateGroup(conversation.id, { name: editedName.trim() });
      setIsEditing(false);
    } catch (error) {
      console.error('[GroupInfoModal] Failed to update group name:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editedName, conversation.groupName, conversation.id, updateGroup]);

  /**
   * Handle canceling edit
   */
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedName(conversation.groupName || '');
  }, [conversation.groupName]);

  /**
   * Handle removing a member
   */
  const handleRemoveMember = useCallback(async (memberId: string) => {
    setRemovingMemberId(memberId);
    try {
      await removeMemberFromGroup(conversation.id, memberId);
    } catch (error) {
      console.error('[GroupInfoModal] Failed to remove member:', error);
    } finally {
      setRemovingMemberId(null);
    }
  }, [conversation.id, removeMemberFromGroup]);

  /**
   * Handle leaving group
   */
  const handleLeaveGroup = useCallback(async () => {
    setIsLeaving(true);
    try {
      await leaveGroup(conversation.id);
      setShowLeaveConfirm(false);
      onOpenChange(false);
    } catch (error) {
      console.error('[GroupInfoModal] Failed to leave group:', error);
    } finally {
      setIsLeaving(false);
    }
  }, [conversation.id, leaveGroup, onOpenChange]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent variant="light" className="max-w-md max-h-[80vh] flex flex-col p-0 gap-0">
          {/* Header with group avatar and name */}
          <DialogHeader className="p-6 pb-4 text-center">
            <div className="flex flex-col items-center">
              {/* Group Avatar */}
              <Avatar className="h-20 w-20 mb-4">
                {conversation.groupAvatar ? (
                  <AvatarImage src={conversation.groupAvatar} alt={conversation.groupName} />
                ) : null}
                <AvatarFallback
                  style={{
                    backgroundColor: 'hsl(var(--primary) / 0.1)',
                    color: 'hsl(var(--primary))',
                  }}
                  className="text-2xl font-semibold"
                >
                  <Users className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>

              {/* Group Name (editable) */}
              {isEditing ? (
                <div className="flex items-center gap-2 w-full max-w-[250px]">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="text-center"
                    maxLength={100}
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSaveName}
                    disabled={isSaving}
                    className="text-green-600 hover:text-green-700"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="text-gray-500"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  {conversation.groupName}
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Edit group name"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </DialogTitle>
              )}

              <p className="text-sm text-gray-500 mt-1">
                {memberCount} member{memberCount !== 1 ? 's' : ''}
              </p>
            </div>
          </DialogHeader>

          {/* Add members button */}
          <div className="px-6 pb-4">
            <Button
              variant="outline"
              className="w-full justify-center gap-2"
              onClick={() => setShowAddMembers(true)}
            >
              <UserPlus className="h-4 w-4" />
              Add Members
            </Button>
          </div>

          {/* Members list */}
          <div className="px-6 pb-2">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Members</h3>
          </div>
          <ScrollArea className="flex-1 min-h-[150px] max-h-[250px]">
            <div className="px-6 pb-4 space-y-1">
              {members.map((participant) => (
                <MemberListItem
                  key={participant.id}
                  participant={participant}
                  isCreator={participant.id === conversation.createdBy}
                  isCurrentUser={participant.id === currentUser?.id}
                  canRemove={isCreator}
                  onRemove={() => handleRemoveMember(participant.id)}
                  isRemoving={removingMemberId === participant.id}
                />
              ))}
            </div>
          </ScrollArea>

          {/* Footer with leave button */}
          <div className="p-6 pt-4 border-t bg-gray-50">
            <Button
              variant="ghost"
              className="w-full justify-center gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setShowLeaveConfirm(true)}
            >
              <LogOut className="h-4 w-4" />
              Leave Group
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave confirmation dialog */}
      <AlertDialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Group?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave "{conversation.groupName}"? You will no longer
              receive messages from this group.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLeaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLeaveGroup}
              disabled={isLeaving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLeaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Leaving...
                </>
              ) : (
                'Leave Group'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add members modal */}
      <AddMembersModal
        open={showAddMembers}
        onOpenChange={setShowAddMembers}
        conversation={conversation}
      />
    </>
  );
};

export default GroupInfoModal;
